import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Deposit,
	Order,
	OrderBookingData,
	OrdersState,
	TFieldsForUpdateOrder,
} from './types';
import { PersonFields } from 'entities/person';
import { formatWithLocale } from 'shared/lib/datepicker';
import { formatTime } from 'shared/lib/time';
import { getOrderByDateTimeReducer } from './extraReducers/getOrdersByDateTime';
import { getOrdersByTableReducer } from './extraReducers/getOrdersByTable';
import { getOrderReducer } from './extraReducers/getOrder';
import { getOrdersByPersonReducer } from './extraReducers/getOrdersByPerson';
import { ordersStates } from './states/orders';
import { bookingStates } from './states/booking';
import { loadersStates } from './states/loaders';
import { getWaitingOrdersReducer } from './extraReducers/getWaitingOrders';
import { getOrdersByFilterReducer } from './extraReducers/getOrdersByFilter';
import { getOrdersNextReducer } from './extraReducers/getOrdersNext';
import { getOrderByDateReducer } from './extraReducers/getOrdersByDate';
import { depositeState } from './states/deposite';
import { getPaymentProvidersReducer } from './extraReducers/getPaymentProviders';
import { getDepositReducer } from './extraReducers/getDeposit';
import { getOrderInfoReducer } from './extraReducers/getOrderInfo';

const initialState: OrdersState = {
	...(ordersStates as OrdersState),
	...(bookingStates as OrdersState),
	...(loadersStates as OrdersState),
	...(depositeState as OrdersState),
	error: null,
	errorOrdersByPerson: null,
	reset: false,
};

export const ordersSlice = createSlice({
	name: 'orders',
	initialState,
	reducers: {
		updateBookingDate: (state, action: PayloadAction<{ date: string, log?: boolean }>) => {
      if (action.payload.log) {
        console.trace();
        console.log('action.payload.date', action.payload.date);
      }
			state.orderBookingData.dt_start = action.payload.date;
		},
		updateBookingDuration: (
			state,
			action: PayloadAction<{ duration: number }>,
		) => {
			state.orderBookingData.duration = action.payload.duration;
		},
		updateBookingParticipants: (
			state,
			action: PayloadAction<{ participants: number }>,
		) => {
			state.orderBookingData.participants_count =
				action.payload.participants;
		},
		updateBookingTags: (
			state,
			action: PayloadAction<{ tags: string[] }>,
		) => {
			state.orderBookingData.tags = action.payload.tags;
		},
		updateBookingNotes: (
			state,
			action: PayloadAction<{ notes: string }>,
		) => {
			state.orderBookingData.notes = action.payload.notes;
		},
		updateBookingDeposit: (
			state,
			action: PayloadAction<{ prepayment_amount: string }>,
		) => {
			state.orderBookingData.prepayment_amount = action.payload.prepayment_amount;
		},
		updateBookingDepositObject: (
			state,
			action: PayloadAction<Deposit>,
		) => {
			state.orderBookingData.deposit = action.payload;
		},
		resetBookingDepositObject: (
			state,
		) => {
			state.orderBookingData.deposit = null;
		},
		updateBookingDepositAllow: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.allowDepositeActions = action.payload;
		},
		updateActivePaymentProvider: (
			state,
			action: PayloadAction<number>,
		) => {
      const provider = state.paymentProviders.find(el=>el.id===action.payload);
      if (provider) {
        state.activeProvider = provider;
      }
		},
		updateBookingResources: (
			state,
			action: PayloadAction<{ resources: number[] }>,
		) => {
			state.orderBookingData.resources = action.payload.resources;
		},
		updateBookingId: (state, action: PayloadAction<{ id: number }>) => {
			state.orderBookingData.id = action.payload.id;
		},

		updateBookingPersonName: (
			state,
			action: PayloadAction<{ name: string }>,
		) => {
			state.orderBookingData.person.name = action.payload.name;
		},
		updateBookingPersonPhone: (
			state,
			action: PayloadAction<{ phone: string }>,
		) => {
			state.orderBookingData.person.phone = action.payload.phone;
		},
		updateBookingPersonNotes: (
			state,
			action: PayloadAction<{ notes: string }>,
		) => {
			state.orderBookingData.person.notes = action.payload.notes;
		},
		updateBookingPersonTags: (
			state,
			action: PayloadAction<{ tags: string[] }>,
		) => {
			state.orderBookingData.person.tags = action.payload.tags;
		},
		updateBookingPersonId: (
			state,
			action: PayloadAction<{ id: number }>,
		) => {
			state.orderBookingData.person.id = action.payload.id;
		},

		updateByPass: (state, action: PayloadAction<{ isByPass: boolean }>) => {
			state.orderBookingData.bypass = action.payload.isByPass;
		},

		updateHoldResources: (state, action: PayloadAction<{ hold_resources: boolean }>) => {
			state.orderBookingData.hold_resources = action.payload.hold_resources;
		},
		resetOrderDetail: state => {
			state.orderDetail = null;
		},
		resetOrderDetailPerson: state => {
			state.orderDetailPerson = null;
		},
		updateOrderDetail: (state, action: PayloadAction<Order>) => {
			state.orderDetail = action.payload;
		},
		updateReset: (state, action: PayloadAction<boolean>) => {
			state.reset = action.payload;
		},
		restoreBookingData: (state, action: PayloadAction<OrderBookingData>) => {
			state.orderBookingData = action.payload;
		},
		resetBookingData: state => {
      state.orderBookingData.dt_start = '';
      state.orderBookingData.duration = 0;
      state.orderBookingData.participants_count = 0;
      state.orderBookingData.notes = '';
      state.orderBookingData.prepayment_amount = '';
      state.orderBookingData.hold_resources = false;
      state.orderBookingData.person = {
        tags: [],
        name: '',
        phone: '',
        notes: '',
        visits: 0,
        cancelled: 0,
        dt_last_visit: '',
      }
      state.orderBookingData.resources = [];
      state.orderBookingData.tags = [];
      state.orderBookingData.bypass = false;
		},
		resetBookingPerson: state => {
			state.orderBookingData.person = {
				tags: [],
				name: '',
				phone: '',
				notes: '',
				visits: 0,
				cancelled: 0,
				dt_last_visit: '',
			};
		},
		deleteBookingDepositObject: (
			state,
		) => {
			state.orderBookingData.deposit = undefined;
      state.activeProvider = null;
		},
		deselectOrderId: state => {
			state.selectedOrderId = 0;
		},
		setLoadedTable: (
			state,
			action: PayloadAction<{ tableName: string }>,
		) => {
			state.loadedTable = action.payload.tableName;
		},
		clearLoadedTable: state => {
			state.ordersByTable = [];
			state.loadedTable = null;
		},
		setBookingThreadMod: (
			state,
			action: PayloadAction<{ isWork: boolean }>,
		) => {
			state.bookingThreadMod = action.payload.isWork;
		},
		setBookingApplicationMod: (
			state,
			action: PayloadAction<{ isWork: boolean }>,
		) => {
			state.bookingApplicationMod = action.payload.isWork;
		},
		setBookingIsNew: (
			state,
			action: PayloadAction<{ isNew: boolean }>,
		) => {
			state.bookingIsNew = action.payload.isNew;
		},
		setBookingIsEdit: (
			state,
			action: PayloadAction<{ isEdit: boolean }>,
		) => {
			state.bookingIsEdit = action.payload.isEdit;
		},
		updateOrderGroupItem: (
			state,
			action: PayloadAction<{
				id: number;
				updates: TFieldsForUpdateOrder;
			}>,
		) => {
			const { id, updates } = action.payload;
			state.ordersGroups.map(orderGroup => {
				const date = formatWithLocale(
					new Date(updates.dt_start),
					'dd MMMM yyyy',
				);
				const formattedTime = formatTime({ time: updates.dt_start });
				if (date === orderGroup.date && formattedTime) {
					const order = orderGroup.orders.find(
						order => order.time === formattedTime.time,
					);
					const findedOrder = order?.ordersList.find(
						order => order.id === id,
					);
					if (findedOrder) {
						Object.entries(updates).map(([key, value]) => {
							findedOrder[key] = value;
						});
					}
				}
			});
		},
		updateOrderGroupItemMhz: (
			state,
			action: PayloadAction<{
				id: number;
				updates: TFieldsForUpdateOrder;
			}>,
		) => {
			const { id, updates } = action.payload;
      const findedGroupIndex = state.ordersGroups.findIndex((orderGroup) => {
        	const date = formatWithLocale(
        		new Date(updates.dt_start),
        		'dd MMMM yyyy',
        	);
          // if (date === orderGroup.date && formattedTime) return index;
          return date === orderGroup.date;
      })

      const formattedTime = formatTime({ time: updates.dt_start });
      if (findedGroupIndex < 0 || !formattedTime) return;

      const findedOrderListIndex = state.ordersGroups[findedGroupIndex].orders.findIndex(order => order.time === formattedTime.time)
      if (findedOrderListIndex < 0) return;

      const currentOrderIndex = state.ordersGroups[findedGroupIndex].orders[findedOrderListIndex].ordersList.findIndex(order => order.id === id);
      if (currentOrderIndex < 0) return;

      Object.entries(updates).map(([key, value]) => {
        state.ordersGroups[findedGroupIndex].orders[findedOrderListIndex].ordersList[currentOrderIndex][key] = value;
      });
		},
		updateWaitingOrderItem: (
			state,
			action: PayloadAction<{
				id: number;
				updates: OrderBookingData;
			}>,
		) => {
			const { id, updates } = action.payload;
			const order = state.waitingOrders.find(order => order.id === id);
			if (order) {
				Object.entries(updates).map(([key, value]) => {
					order[key] = value;
				});
			}
		},
		updatePersonDataInWaitingOrderItem: (
			state,
			action: PayloadAction<{
				id: number;
				updates: PersonFields;
			}>,
		) => {
			const { id, updates } = action.payload;
			const order = state.waitingOrders.find(order => order.id === id);
			if (order) {
				const person = { ...order?.person, ...updates };
				if (order.person) {
					order['person'] = person;
				}
			}
		},
		updateOrdersSearchNext: (state, action: PayloadAction<string>) => {
			state.ordersSearchNext = action.payload;
		},
		clearOrdersByDate: (state) => {
			state.ordersByDate = [];
		},
    setInitialBookingData: (state) => {
      if (!state.orderBookingDataInitial) {
        state.orderBookingDataInitial = state.orderBookingData;
      }
    },
    setToBookingButtonDisabled: (state, action:PayloadAction<boolean>) => {
      state.toBookingButtonDisabled = action.payload;
    }
	},
	extraReducers: builder => {
		getOrderByDateTimeReducer(builder)
		getOrdersByFilterReducer(builder)
		getOrdersByTableReducer(builder)
		getWaitingOrdersReducer(builder)
		getOrderReducer(builder)
		getOrderInfoReducer(builder)
		getOrdersByPersonReducer(builder)
		getOrdersNextReducer(builder)
		getOrderByDateReducer(builder)
		getPaymentProvidersReducer(builder)
    getDepositReducer(builder)
	},
});

export const ordersReducer = ordersSlice.reducer;
export const {
	updateBookingDate,
	updateBookingDuration,
	updateBookingParticipants,
	updateBookingPersonName,
	updateBookingPersonPhone,
	updateBookingPersonNotes,
	updateBookingPersonTags,
	updateBookingTags,
	updateBookingNotes,
	updateBookingDeposit,
  updateBookingDepositObject,
  resetBookingDepositObject,
	resetOrderDetail,
	resetOrderDetailPerson,
	updateOrderDetail,
	updateBookingResources,
	updateBookingPersonId,
	updateBookingId,
	resetBookingData,
	resetBookingPerson,
	deselectOrderId,
	updateByPass,
	updateHoldResources,
	clearLoadedTable,
	setLoadedTable,
	setBookingThreadMod,
	updateOrderGroupItem,
	updateWaitingOrderItem,
	updatePersonDataInWaitingOrderItem,
	setBookingApplicationMod,
  setBookingIsNew,
  setBookingIsEdit,
	updateOrdersSearchNext,
	clearOrdersByDate,
	restoreBookingData,
	updateReset,
  setInitialBookingData,
  deleteBookingDepositObject,
  updateActivePaymentProvider,
  updateBookingDepositAllow,
  setToBookingButtonDisabled,
  updateOrderGroupItemMhz
} = ordersSlice.actions;
